import React from 'react';
import { ConditionConfiguration } from '../../../../types/workflow';

interface ConditionConfigProps {
  configuration: ConditionConfiguration;
  onChange: (config: ConditionConfiguration) => void;
  isReadOnly: boolean;
}

const ConditionConfig: React.FC<ConditionConfigProps> = ({
  configuration,
  onChange,
  isReadOnly,
}) => {
  const updateConfig = (field: keyof ConditionConfiguration, value: any) => {
    onChange({
      ...configuration,
      [field]: value,
    });
  };

  const addBranch = () => {
    const branches = configuration.branches || [];
    updateConfig('branches', [
      ...branches,
      { label: `Branch ${branches.length + 1}`, condition: '' }
    ]);
  };

  const updateBranch = (index: number, field: 'label' | 'condition', value: string) => {
    const branches = [...(configuration.branches || [])];
    branches[index] = { ...branches[index], [field]: value };
    updateConfig('branches', branches);
  };

  const removeBranch = (index: number) => {
    const branches = configuration.branches || [];
    updateConfig('branches', branches.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Default Expression
        </label>
        <textarea
          value={configuration.expression || ''}
          onChange={(e) => updateConfig('expression', e.target.value)}
          disabled={isReadOnly}
          placeholder="Enter JavaScript expression (e.g., context.amount > 1000)"
          rows={3}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm font-mono disabled:bg-gray-100"
        />
        <p className="mt-1 text-xs text-gray-500">
          JavaScript expression that evaluates to true/false
        </p>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Branches
          </label>
          {!isReadOnly && (
            <button
              type="button"
              onClick={addBranch}
              className="text-sm text-primary-600 hover:text-primary-500"
            >
              + Add Branch
            </button>
          )}
        </div>
        
        <div className="space-y-3">
          {(configuration.branches || []).map((branch, index) => (
            <div key={index} className="border border-gray-200 rounded-md p-3">
              <div className="flex items-start justify-between">
                <div className="flex-1 space-y-2">
                  <input
                    type="text"
                    value={branch.label}
                    onChange={(e) => updateBranch(index, 'label', e.target.value)}
                    disabled={isReadOnly}
                    placeholder="Branch label"
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm disabled:bg-gray-100"
                  />
                  <textarea
                    value={branch.condition}
                    onChange={(e) => updateBranch(index, 'condition', e.target.value)}
                    disabled={isReadOnly}
                    placeholder="Condition expression"
                    rows={2}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm font-mono disabled:bg-gray-100"
                  />
                </div>
                {!isReadOnly && (
                  <button
                    type="button"
                    onClick={() => removeBranch(index)}
                    className="ml-2 text-red-600 hover:text-red-500"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-blue-50 p-3 rounded-md">
        <p className="text-xs text-blue-700">
          <strong>Available Variables:</strong>
        </p>
        <ul className="mt-1 text-xs text-blue-600 list-disc list-inside">
          <li>context - Workflow context variables</li>
          <li>input - Node input data</li>
          <li>user - Current user information</li>
          <li>entity - Parent entity data (if applicable)</li>
        </ul>
      </div>
    </div>
  );
};

export default ConditionConfig;